import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Grid,
  Paper,
  Chip,
  Button,
  Tabs,
  Tab,
} from "@mhome/ui";
import { useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import TuneIcon from "@mui/icons-material/Tune";
import { useRecoilValue } from "recoil";
import {
  activeScopeState,
  scopePreferenceState,
  safeAreaInsetsState,
} from "../global-state";
import { useCustomFormat } from "../views/after-init";
import dayjs from "dayjs";
import NumberInput from "../input/number-input";
import NumberUnitInput from "../input/number-unit-input";
import ReactECharts from "echarts-for-react";
import EntityAutomation from "../automation/entity-automation";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const UNIT_MAPPINGS = {
  temperature: {
    units: ["C", "F"],
    steps: [0.5, 1],
    displayUnits: ["°C", "°F"],
    defaultUnit: "C",
  },
  temperatureDifference: {
    units: ["dC", "dF"],
    steps: [0.5, 1],
    displayUnits: ["Δ°C", "Δ°F"],
    defaultUnit: "dC",
  },
};

const CircadianDetailDialog = ({
  open,
  onClose,
  widget,
  onEdit,
  onUpdate, // New prop to handle updates
}) => {
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const activeScope = useRecoilValue(activeScopeState);
  const scopePreference = useRecoilValue(scopePreferenceState);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const customFormat = useCustomFormat();
  const getComponentBgColor = useComponentBackgroundColor();

  // Helper function to convert MUI spacing to pixels
  const spacingToPx = (value) => {
    if (typeof value === "number") {
      return `${value * 8}px`;
    }
    return value;
  };

  // Local state for editing
  const [multiplier, setMultiplier] = useState(1);
  const [offset, setOffset] = useState("");
  const [activeCurve, setActiveCurve] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Track original values to detect changes
  const [originalMultiplier, setOriginalMultiplier] = useState(1);
  const [originalOffset, setOriginalOffset] = useState("");

  // Initialize state
  useEffect(() => {
    if (widget) {
      const widgetMultiplier =
        widget.multiplier !== undefined ? widget.multiplier : 1;
      const widgetOffset = widget.offset !== undefined ? widget.offset : "";

      setMultiplier(widgetMultiplier);
      setOffset(widgetOffset);
      setActiveCurve(
        widget.curve || Object.keys(widget.schedules || {})[0] || ""
      );

      // Store original values
      setOriginalMultiplier(widgetMultiplier);
      setOriginalOffset(widgetOffset);
    }
  }, [widget]);

  if (!widget) return null;

  // Handle updates
  const handleMultiplierChange = (val) => {
    const newMultiplier = parseFloat(val);
    setMultiplier(val);

    // Only call API if value actually changed
    if (newMultiplier !== originalMultiplier) {
      setOriginalMultiplier(newMultiplier);
      onUpdate && onUpdate({ ...widget, multiplier: newMultiplier });
    }
  };

  const handleOffsetChange = (val) => {
    setOffset(val);

    // Only call API if value actually changed
    if (val !== originalOffset) {
      setOriginalOffset(val);
      onUpdate && onUpdate({ ...widget, offset: val });
    }
  };

  const handleNumberUnitOffsetChange = (val, unit) => {
    const newValue = `${val}${unit}`;
    setOffset(newValue);

    // Only call API if value actually changed
    if (newValue !== originalOffset) {
      setOriginalOffset(newValue);
      onUpdate && onUpdate({ ...widget, offset: newValue });
    }
  };

  // Helper functions for inputs
  const shouldUseNumberUnitInput = (tag) => {
    return tag === "temperature" || tag === "temperatureDifference";
  };

  const getDataTypeFromTagForInput = (tag) => {
    switch (tag) {
      case "temperature":
        return "Temperature";
      case "temperatureDifference":
        return "TemperatureDiff";
      default:
        return "Number";
    }
  };

  const createMultiplierNumberInputSpec = () => ({
    value: (parseFloat(multiplier) || 1).toString(),
    min: 0.1,
    max: null,
    step: 0.1,
    precision: 2,
    readOnly: false,
  });

  const createOffsetNumberInputSpec = () => {
    const numValue = parseFloat(offset) || 0;
    let step = 1;
    if (widget.tag === "colorTemperature") step = 50;

    return {
      value: numValue.toString(),
      min: null,
      max: null,
      step,
      precision: 0,
      readOnly: false,
    };
  };

  const createNumberUnitInputSpec = () => {
    const unitConfig = UNIT_MAPPINGS[widget.tag] || UNIT_MAPPINGS.temperature;
    const currentValue = offset || `0${unitConfig.defaultUnit}`;

    const parseValue = (val) => {
      if (!val) return { number: 0, unit: unitConfig.defaultUnit };
      const match = val.match(/^([+-]?[0-9.]+)(°?[A-Za-z]+)$/);
      if (match) {
        return {
          number: parseFloat(match[1]),
          unit: match[2].replace("°", ""),
        };
      }
      return { number: parseFloat(val) || 0, unit: unitConfig.defaultUnit };
    };

    const parsed = parseValue(currentValue);

    return {
      dataType: getDataTypeFromTagForInput(widget.tag),
      value: currentValue,
      number: parsed.number,
      unit: parsed.unit,
      units: unitConfig.units,
      steps: unitConfig.steps,
      stepUnits: unitConfig.units,
      min: null,
      max: null,
      readOnly: false,
    };
  };

  // Chart generation logic
  const getChartOption = () => {
    const schedule = widget.schedules?.[activeCurve] || [];

    // Parse time to minutes for sorting
    const parseTimeToMinutes = (timeStr) => {
      // Handle SUNRISE/SUNSET with offsets
      if (timeStr.startsWith("SUNRISE")) {
        const match = timeStr.match(/SUNRISE([+-]\d+min)?/);
        const offset = match && match[1] ? parseInt(match[1]) : 0;
        return 360 + offset; // Assume 6:00 AM (360 minutes) + offset
      }
      if (timeStr.startsWith("SUNSET")) {
        const match = timeStr.match(/SUNSET([+-]\d+min)?/);
        const offset = match && match[1] ? parseInt(match[1]) : 0;
        return 1080 + offset; // Assume 6:00 PM (1080 minutes) + offset
      }
      // Parse HH:mm:ss
      const parts = timeStr.split(":");
      if (parts.length >= 2) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
      return 0;
    };

    // Format time for display
    const formatTimeDisplay = (timeStr) => {
      if (timeStr.startsWith("SUNRISE"))
        return timeStr.replace("SUNRISE", "SR");
      if (timeStr.startsWith("SUNSET")) return timeStr.replace("SUNSET", "SS");
      const parts = timeStr.split(":");
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : timeStr;
    };

    // Sort schedule by time
    const sortedSchedule = [...schedule].sort((a, b) => {
      return parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time);
    });

    const data = sortedSchedule.map((item) => parseFloat(item.value) || 0);
    const times = sortedSchedule.map((item) => formatTimeDisplay(item.time));

    return {
      grid: { top: 30, right: 15, bottom: 40, left: 40 },
      xAxis: {
        type: "category",
        data: times,
        axisLabel: {
          rotate: 45,
          fontSize: 10,
        },
        axisLine: {
          lineStyle: {
            color: "hsl(var(--border))",
          },
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: true,
          lineStyle: {
            color: "hsl(var(--border))",
            opacity: 0.3,
          },
        },
        axisLabel: {
          fontSize: 10,
        },
      },
      series: [
        {
          data: data.map((value, index) => ({
            value,
            symbol: "circle",
            symbolSize: 6,
            itemStyle: {
              borderColor: "hsl(var(--card))",
              borderWidth: 2,
            },
          })),
          type: "line",
          smooth: false, // Disable smooth for more accurate representation
          lineStyle: {
            color: "hsl(var(--primary))",
            width: 2,
          },
          areaStyle: {
            color: "hsl(var(--primary))",
            opacity: 0.08,
          },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {},
        },
        formatter: function (params) {
          if (params && params.length > 0) {
            const param = params[0];
            return `${param.name}<br/>${formatValue(param.value, widget.tag)}`;
          }
          return "";
        },
      },
    };
  };

  // Format value helper
  const formatValue = (value, tag) => {
    if (value === undefined || value === null) return "N/A";

    switch (tag) {
      case "brightness":
        return `${Math.round(parseFloat(value) || 0)}%`;
      case "colorTemperature":
        return `${Math.round(parseFloat(value) || 0)}K`;
      case "colorHue":
        return `${Math.round(parseFloat(value) || 0)}°`;
      case "temperature":
      case "temperatureDifference": {
        const preferredUnit =
          scopePreference?.[activeScope]?.temperatureUnit || "C";
        if (!value) value = "0" + preferredUnit;
        const match = value.match(/^([+-]?[0-9.]+)([CF])$/);
        if (!match) {
          const numValue = parseFloat(value);
          return `${Math.round(numValue)}°${preferredUnit}`;
        }
        const [, numStr, unit] = match;
        const numValue = parseFloat(numStr);
        let displayValue = numValue;
        let displayUnit = unit;

        if (unit === "C" && preferredUnit === "F") {
          displayValue = (numValue * 9) / 5 + 32;
          displayUnit = "F";
        } else if (unit === "F" && preferredUnit === "C") {
          displayValue = ((numValue - 32) * 5) / 9;
          displayUnit = "C";
        } else {
          displayUnit = preferredUnit;
        }
        return `${Math.round(displayValue)}°${displayUnit}`;
      }
      default:
        return Math.round(parseFloat(value) || 0).toString();
    }
  };

  const DetailItem = ({ icon, label, value, secondary }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: spacingToPx(2),
        marginBottom: 0,
      }}
    >
      <div
        className="text-muted-foreground"
        style={{
          marginTop: spacingToPx(0.5),
        }}
      >
        {icon}
      </div>
      <div>
        <Typography
          variant="caption"
          className="text-muted-foreground"
          style={{
            display: "block",
          }}
        >
          {label}
        </Typography>
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          {value}
        </Typography>
        {secondary && (
          <Typography variant="caption" className="text-muted-foreground">
            {secondary}
          </Typography>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          margin: 0,
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px`,
          marginBottom: isSmallScreen ? 0 : `${safeAreaInsets.bottom}px`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${safeAreaInsets.top + safeAreaInsets.bottom}px)`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${safeAreaInsets.top + safeAreaInsets.bottom}px)`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
          paddingLeft: `${safeAreaInsets.left}px`,
        },
      }}
    >
      <DialogTitle
        className="flex items-center gap-2"
        style={{
          minHeight: "auto",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose(e);
          }}
          style={{ flexShrink: 0, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{ flex: 1, lineHeight: 1.2 }}
        >
          {widget.name}
        </Typography>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit(widget);
          }}
          color="primary"
          style={{ zIndex: 1 }}
        >
          <EditIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        style={{
          paddingTop: spacingToPx(2),
          paddingLeft: spacingToPx(2),
          paddingRight: spacingToPx(2),
          paddingBottom: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Scrollable Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingTop: spacingToPx(2),
            paddingBottom: spacingToPx(3),
            boxSizing: "border-box",
          }}
        >
          {activeTab === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacingToPx(3),
              }}
            >
              {/* Hero Section */}
              <Paper
                variant="outlined"
                style={{
                  padding: spacingToPx(2),
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    paddingTop: spacingToPx(2),
                    paddingBottom: spacingToPx(2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Current Value
                  </Typography>
                  <Typography
                    variant="h2"
                    component="div"
                    color="primary"
                    style={{
                      fontWeight: 600,
                    }}
                  >
                    {formatValue(widget.value, widget.tag)}
                  </Typography>
                  <Chip
                    label={
                      widget.tag === "temperatureDifference"
                        ? "Temperature Difference"
                        : widget.tag === "colorTemperature"
                        ? "Color Temperature"
                        : widget.tag === "colorHue"
                        ? "Color Hue"
                        : widget.tag.charAt(0).toUpperCase() +
                          widget.tag.slice(1)
                    }
                    size="small"
                    variant="outlined"
                    style={{
                      marginTop: spacingToPx(1),
                      textTransform: "capitalize",
                    }}
                  />
                </div>
              </Paper>

              {/* Row 1: Next Transition & Location */}
              <div
                style={{
                  display: "flex",
                  gap: spacingToPx(2),
                  width: "100%",
                }}
              >
                <Paper
                  variant="outlined"
                  style={{
                    flex: 1,
                    padding: spacingToPx(2),
                    minHeight: spacingToPx(10),
                    backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  }}
                >
                  <DetailItem
                    icon={<AccessTimeIcon />}
                    label="Next Transition"
                    value={
                      widget.nextTimestamp
                        ? customFormat(widget.nextTimestamp, dayjs(), true)
                        : "None"
                    }
                    secondary={
                      widget.next
                        ? `Target: ${formatValue(widget.next, widget.tag)}`
                        : null
                    }
                  />
                </Paper>
                <Paper
                  variant="outlined"
                  style={{
                    flex: 1,
                    padding: spacingToPx(2),
                    minHeight: spacingToPx(10),
                    backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  }}
                >
                  <DetailItem
                    icon={<LocationOnIcon />}
                    label="Location"
                    value={widget.location?.name || "Not Set"}
                  />
                </Paper>
              </div>

              {/* Row 2: Curve Card */}
              <Paper
                variant="outlined"
                style={{
                  padding: spacingToPx(2),
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacingToPx(1),
                  }}
                >
                  <TimelineIcon fontSize="small" /> Curves
                </Typography>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: spacingToPx(2),
                  }}
                >
                  <div
                    style={{
                      borderBottom: "1px solid hsl(var(--border))",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Tabs
                      value={
                        Object.keys(widget.schedules || {}).indexOf(
                          activeCurve
                        ) !== -1
                          ? Object.keys(widget.schedules || {}).indexOf(
                              activeCurve
                            )
                          : 0
                      }
                      onChange={(e, v) => {
                        const keys = Object.keys(widget.schedules || {});
                        setActiveCurve(keys[v]);
                      }}
                      variant="standard"
                      style={{
                        minHeight: 36,
                      }}
                    >
                      {Object.keys(widget.schedules || {}).map(
                        (curveName, index) => {
                          const isActive =
                            Object.keys(widget.schedules || {}).indexOf(
                              activeCurve
                            ) === index;
                          return (
                            <Tab
                              key={curveName}
                              label={curveName}
                              data-tab-index={index}
                              style={{
                                textTransform: "none",
                                minHeight: 36,
                                fontSize: "0.875rem",
                                fontWeight: isActive ? 600 : 500,
                              }}
                            />
                          );
                        }
                      )}
                    </Tabs>
                  </div>

                  <div
                    style={{
                      marginLeft: spacingToPx(2),
                      flexShrink: 0,
                    }}
                  >
                    {widget.curve === activeCurve ? (
                      <Chip
                        label="Active"
                        color="primary"
                        size="small"
                        variant="filled"
                      />
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          onUpdate &&
                            onUpdate({ ...widget, curve: activeCurve });
                        }}
                        style={{ boxShadow: "none" }}
                      >
                        Set Active
                      </Button>
                    )}
                  </div>
                </div>

                <div style={{ height: 180, width: "100%" }}>
                  <ReactECharts
                    option={getChartOption()}
                    style={{ height: "100%", width: "100%" }}
                    opts={{ renderer: "svg" }}
                  />
                </div>
              </Paper>

              {/* Row 3: Fine Tuning */}
              <Paper
                variant="outlined"
                style={{
                  padding: spacingToPx(2),
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacingToPx(1),
                  }}
                >
                  <TuneIcon fontSize="small" /> Fine Tuning
                </Typography>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: spacingToPx(2),
                  }}
                >
                  {widget.tag !== "temperature" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body2"
                        className="text-muted-foreground"
                      >
                        Multiplier
                      </Typography>
                      <div style={{ width: 120 }}>
                        <NumberInput
                          valueSpec={createMultiplierNumberInputSpec()}
                          onChange={handleMultiplierChange}
                          onError={() => {}}
                          size="small"
                        />
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="text-muted-foreground"
                    >
                      Offset
                    </Typography>
                    <div style={{ width: 120 }}>
                      {shouldUseNumberUnitInput(widget.tag) ? (
                        <NumberUnitInput
                          valueSpec={createNumberUnitInputSpec()}
                          onChange={handleNumberUnitOffsetChange}
                          onError={() => {}}
                          size="small"
                        />
                      ) : (
                        <NumberInput
                          valueSpec={createOffsetNumberInputSpec()}
                          onChange={handleOffsetChange}
                          onError={() => {}}
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <Typography
                  variant="caption"
                  className="text-muted-foreground"
                  style={{
                    marginTop: spacingToPx(1),
                    display: "block",
                  }}
                >
                  Adjustments are applied immediately.
                </Typography>
              </Paper>
            </div>
          )}

          {activeTab === 1 && (
            <div style={{ marginTop: spacingToPx(1) }}>
              <EntityAutomation
                title="Related Automations"
                automationFetch={{
                  entityType: "basic.widget.CircadianWidget",
                  integration: "basic.widget",
                  entityId: widget.id,
                  filter: {
                    allCapability: [
                      "basic.widget.command.setCircadianWidgetSetting",
                    ],
                  },
                }}
                inspireFetch={(() => {
                  if (widget.tag === "brightness") {
                    return [
                      {
                        id: "Pwecpb2",
                        args: [
                          {
                            entityType: "basic.widget.CircadianWidget",
                            name: widget?.name,
                          },
                        ],
                      },
                      {
                        id: "PMvuAG8",
                        args: [
                          {
                            entityType: "basic.widget.CircadianWidget",
                            name: widget?.name,
                          },
                        ],
                      },
                    ];
                  } else if (widget.tag === "colorTemperature") {
                    return [
                      {
                        id: "PRrbezV",
                        args: [
                          {
                            entityType: "basic.widget.CircadianWidget",
                            name: widget?.name,
                          },
                        ],
                      },
                      {
                        id: "PYQanv1",
                        args: [
                          {
                            entityType: "basic.widget.CircadianWidget",
                            name: widget?.name,
                          },
                        ],
                      },
                    ];
                  }
                  return null;
                })()}
              />
            </div>
          )}
        </div>

        {/* Fixed Bottom Tab Navigation */}
        <div
          style={{
            position: "relative",
            borderTop: "1px solid hsl(var(--border))",
            backgroundColor: "transparent",
            paddingTop: spacingToPx(1),
            paddingBottom: isSmallScreen
              ? `${safeAreaInsets.bottom}px`
              : spacingToPx(1),
            paddingLeft: spacingToPx(2),
            paddingRight: spacingToPx(2),
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            minHeight: `${8 + 120 + 8}px`,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 120,
            }}
            data-circadian-tabs
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="standard"
              style={{
                minHeight: 48,
                width: "100%",
              }}
            >
              <Tab
                label="Overview"
                data-tab-index="0"
                style={{
                  minHeight: 48,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: activeTab === 0 ? 600 : 500,
                }}
              />
              <Tab
                label="Automations"
                data-tab-index="1"
                style={{
                  minHeight: 48,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: activeTab === 1 ? 600 : 500,
                }}
              />
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CircadianDetailDialog;
